import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { AuthenticatedUserModel } from "@courses-utils/gateway-auth";

import { urls } from "@courses-pm2/api/src/ts/config";
import {
  CaptionedContentElementData,
  IframeData,
  IframeModel,
  IframeTargets,
} from "@courses-pm2/api/src/ts/models";
import { Engine, resolveMediaPath } from "@courses-pm2/api/src/ts/utils";

import { parseCaptionedContentElement } from "@courses-pm2/api/src/ts/parser/parseCaptionedContentElement";
import { ComponentEnvelope, htmlDataSchema } from "@utils/component-framework";

/**
 * Specification of custom meta data that can be applied to iframes using some
 * props differently than other elements.
 */
type IframeConfig = Pick<IframeData, Exclude<keyof IframeData, "target">> &
  Pick<
    CaptionedContentElementData,
    Exclude<keyof CaptionedContentElementData, "portalId">
  >;

/**
 * Specification of the response format when fetching display info about
 * components.
 */
type ComponentDisplayInfoResponse = {
  /**
   * Specifies the id of the component.
   */
  ComponentID: string;

  /**
   * Specifies the type of the component, so the proper client can be
   * identified.
   */
  ComponentType: string;
};

/**
 * Helper that parses different iframe elements in the response from the pm2
 * backend.
 */
export async function parseIframe({
  node,
  user,
  portalId,
  engine,
}: {
  node: Element;
  user?: AuthenticatedUserModel;
  portalId: string;
  engine?: Engine;
}): Promise<IframeModel> {
  // Announce that a model has been identified if possible
  if (engine) {
    engine.announce(IframeModel);
  }

  // Handle different kinds of iframes, extracting their basic configurations
  let data: IframeConfig;

  switch (node.tagName.toLowerCase()) {
    case "component":
      data = await parseComponentIframe(node, user);
      break;

    case "ibook":
    case "ibook3":
      data = parseIbook3Iframe(node);
      break;

    case "ibook4":
      data = parseIbook4Iframe(node);
      break;

    case "taskenginelink":
      data = parseTaskEngineIframe(node);
      break;

    default:
      data = parseHtmlIframe(node);
  }

  // Some iframes support having title and description as custom props
  const title = node.getAttribute("title");
  const description = node.getAttribute("description");

  if (title) {
    data.title = title;
  }

  if (description) {
    data.description = description;
  }

  // Instantiate the model
  return new IframeModel({
    ...data,
    engine,
    portalId,
    target:
      node.getAttribute("openAs") === "new-window"
        ? IframeTargets.WINDOW
        : IframeTargets.LIGHTBOX,
  });
}

/**
 * Helper that parses component iframes.
 */
async function parseComponentIframe(
  node: Element,
  user?: AuthenticatedUserModel,
): Promise<IframeConfig> {
  // Extract core data for captioned content elements
  const coreData = parseCaptionedContentElement(node);

  // Fecth display info needed to properly render the componennt in the client
  const referenceId = node.getAttribute("componentId") || "";

  const displayInfoResponse = await axios.get<ComponentDisplayInfoResponse>(
    gGateway(
      `${urls.componentFramework}/api/References/GetDisplayInfo/${referenceId}`,
    ),
  );

  const displayInfo = displayInfoResponse.data;
  const institutionId = await user?.getInstitutionId("");

  // Generate raw proxy url
  let src = await (async () => {
    try {
      if (displayInfo.ComponentType === "html") {
        const componentDataResponse = await axios.get<
          ComponentEnvelope & { Content: string }
        >(
          gGateway(
            `${urls.componentFramework}/api/Components/Get/${encodeURIComponent(
              displayInfo.ComponentID,
            )}`,
          ),
        );

        const componentData = htmlDataSchema.parse(
          JSON.parse(componentDataResponse.data.Content),
        );

        return `${urls.htmlComponentAssets}/${componentData.selectedAssetId}/index.html`;
      }
    } catch (err) {
      console.error(err);
    }

    return (
      `${urls.componentFramework}/Proxy/ComponentViewer` +
      `?userID=${encodeURIComponent(user ? user.id : "guest")}` +
      `&institutionID=${encodeURIComponent(institutionId ?? "")}` +
      `&componentID=${encodeURIComponent(displayInfo.ComponentID)}` +
      `&componentType=${encodeURIComponent(displayInfo.ComponentType)}`
    );
  })();

  // If the iframe has been marked as a learning object, then make sure that
  // this info is going to be properly propagated
  if (coreData.isStandalone && coreData.learningObject) {
    src += `&learningObjectId=${encodeURIComponent(
      coreData.learningObject.id,
    )}`;
    src += `&learningObjectTitle=${encodeURIComponent(
      coreData.learningObject.title,
    )}`;
  }

  // Determine desired proportions of the iframe, depending on the kind of
  // component we're going to display
  let aspectRatio: {
    height: number;
    width: number;
  };

  let minSize: {
    height: number;
    width: number;
  };

  switch (displayInfo.ComponentType) {
    case "i-log":
      aspectRatio = minSize = {
        height: 600,
        width: 1000,
      };
      break;

    case "dilemma-spillet":
      aspectRatio = minSize = {
        height: 600,
        width: 1000,
      };
      break;

    case "html":
      aspectRatio = minSize = {
        height: 600,
        width: 800,
      };
      break;

    case "arbejdsark":
      aspectRatio = minSize = {
        height: 600,
        width: 750,
      };
      break;

    case "i-guide":
      aspectRatio = {
        height: 600,
        width: 1020,
      };

      minSize = {
        height: 300,
        width: 300,
      };
      break;

    case "opgavekompleks-simpel":
    case "opgavekompleks-flex":
      aspectRatio = {
        height: 600,
        width: 850,
      };

      minSize = {
        height: 650,
        width: 770,
      };
      break;

    case "beriget-video":
      aspectRatio = {
        height: 350,
        width: 600,
      };

      minSize = {
        height: 300,
        width: 500,
      };
      break;

    default:
      aspectRatio = {
        height: 9,
        width: 16,
      };

      minSize = {
        height: 600,
        width: 800,
      };
  }

  const previewImage = resolveMediaPath(node.getAttribute("previewImage"));

  return {
    aspectRatio,
    autoActivate: node.getAttribute("autoActivate") === "true",
    inPageEmbed: true,
    maxSize: {
      height: 0,
      width: 0,
    },
    minSize,
    previewImage,
    needsAutoGeneratedPreviewImage: !previewImage,
    src,

    // Propagate the core data
    ...coreData,
  };
}

/**
 * Helper that parses ibook3 iframes.
 */
function parseIbook3Iframe(node: Element): IframeConfig {
  // Extract core data for captioned content elements
  const coreData = parseCaptionedContentElement(node);

  const previewImage = resolveMediaPath(node.getAttribute("previewImage"));

  // Extract data regarding the iframe
  return {
    aspectRatio: parseHeightWidth(node.getAttribute("aspectRatio") || "16-9"),
    autoActivate: true,
    inPageEmbed: true,
    maxSize: parseHeightWidth(node.getAttribute("maxSize") || "0-0"),
    minSize: {
      height: 575,
      width: 800,
    },
    previewImage,
    needsAutoGeneratedPreviewImage: !previewImage,
    src: `${urls.ibook3}/embed/?id=${encodeURIComponent(
      node.getAttribute("bookId") || "",
    )}`,

    // Propagate the core data
    ...coreData,
  };
}

/**
 * Helper that parses ibook4 iframes.
 */
function parseIbook4Iframe(node: Element): IframeConfig {
  // Extract core data for captioned content elements
  const coreData = parseCaptionedContentElement(node);

  // Force the "open in new window"-option to be added to the toolbar of the
  // component
  if (coreData.toolbar) {
    coreData.toolbar.enableNewWindow = true;
  }

  const previewImage = resolveMediaPath(node.getAttribute("previewImage"));

  // Extract different aspect ratios and sizes
  return {
    aspectRatio: parseHeightWidth(node.getAttribute("aspectRatio") || "16-9"),
    autoActivate: true,
    inPageEmbed: true,
    maxSize: parseHeightWidth(node.getAttribute("maxSize") || "0-0"),
    minSize: {
      height: 575,
      width: 800,
    },
    previewImage,
    needsAutoGeneratedPreviewImage: !previewImage,
    src: `${urls.ibook4}/${encodeURIComponent(
      node.getAttribute("bookId") || "",
    )}/?embed=1`,

    // Propagate the core data
    ...coreData,
  };
}

/**
 * Helper that parses task engine iframes.
 */
function parseTaskEngineIframe(node: Element): IframeConfig {
  // Extract core data for captioned content elements
  const coreData = parseCaptionedContentElement(node);

  const previewImage = resolveMediaPath(node.getAttribute("previewImage"));

  // Extract different aspect ratios and sizes
  return {
    aspectRatio: parseHeightWidth(node.getAttribute("aspectRatio") || "16-9"),
    autoActivate: false,
    inPageEmbed: false,
    maxSize: parseHeightWidth(node.getAttribute("maxSize") || "0-0"),
    minSize: parseHeightWidth(node.getAttribute("minSize") || "1-1"),
    previewImage: resolveMediaPath(node.getAttribute("previewImage")),
    needsAutoGeneratedPreviewImage: !previewImage,
    src: node.getAttribute("url") || "",

    // Propagate the core data
    ...coreData,
  };
}

/**
 * Helper that parses plain iframes embedded within html feeds.
 */
function parseHtmlIframe(node: Element): IframeConfig {
  // Extract core data for captioned content elements
  const coreData = parseCaptionedContentElement(node);

  // Extract width / height ratio
  const width = parseInt(node.getAttribute("width") || "", 10) || 600;
  const height =
    parseInt(node.getAttribute("height") || "", 10) ||
    Math.round((width / 3) * 2);

  const size = {
    height,
    width,
  };

  // Extract different aspect ratios and sizes
  return {
    aspectRatio: size,
    autoActivate: true,
    inPageEmbed: true,
    maxSize: size,
    minSize: size,
    previewImage: undefined,
    needsAutoGeneratedPreviewImage: true,
    src: node.getAttribute("src") || "",

    // Propagate the core data
    ...coreData,
  };
}

/**
 * Helper that extracts height/width relationship from a string containing the
 * values as [width]-[height].
 */
function parseHeightWidth(ratio: string): {
  height: number;
  width: number;
} {
  const parts = ratio.split("-");

  return {
    height: parseInt(parts[1], 10),
    width: parseInt(parts[0], 10),
  };
}
